// STAGING-LAUNCH-READINESS-002-R2 — customer cabinet: "Мої замовлення" list.
// STAGING-LAUNCH-READINESS-002-R2-R2 — stale-while-revalidate: seed from the
// in-memory cache so "back" shows the list instantly (no spinner replay), then
// silently refresh. Bounded load with an 8s degraded (Retry + Home) fallback
// instead of an infinite spinner.
import { useEffect, useMemo, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  fetchMyOrdersBounded,
  getCachedOrdersList,
  prefetchOrderDetail,
  type CustomerOrderSummary,
} from "./order-history-v2.js";
import { receiptStateLabel } from "./order-status-copy-v2.js";
import {
  canonicalDisplayChipUkForOrder,
  canonicalListSubtitleUk,
  hideCustomerReceiptLine,
  projectCanonicalDisplayState,
} from "./canonical-display-state-v2.js";
import {
  canonicalOrderTitle,
  mapToCanonicalCustomerStage,
} from "./canonical-order-projection-v2.js";
import { formatUah } from "./money-v2.js";
import { t } from "./store-v2-ui-copy.js";
import { LoadingBlock, EmptyState, OrderItemThumb } from "./components.js";
import { useRouteLoadingGate } from "./route-loading-gate-v2.js";
import { emitEvent } from "./events-v2.js";

import { formatOrderReceivedCardTime, getOrderReceivedAt } from "./order-received-at.js";

function formatOrderDate(iso: string, lang: StoreV2Lang): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function orderDateGroupKey(iso: string, lang: StoreV2Lang): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return lang === "en" ? "Earlier" : "Раніше";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const orderDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (orderDay.getTime() === today.getTime()) return lang === "en" ? "Today" : "Сьогодні";
  if (orderDay.getTime() === yesterday.getTime()) return lang === "en" ? "Yesterday" : "Вчора";
  return formatOrderDate(iso, lang);
}

function groupOrdersByDate(
  orders: CustomerOrderSummary[],
  lang: StoreV2Lang,
): Array<{ label: string; orders: CustomerOrderSummary[] }> {
  const groups: Array<{ label: string; orders: CustomerOrderSummary[] }> = [];
  const indexByLabel = new Map<string, number>();
  for (const order of orders) {
    const label = orderDateGroupKey(order.created_at, lang);
    const idx = indexByLabel.get(label);
    if (idx == null) {
      indexByLabel.set(label, groups.length);
      groups.push({ label, orders: [order] });
    } else {
      groups[idx]!.orders.push(order);
    }
  }
  return groups;
}

export function MyOrdersV2({
  lang,
  sessionKey,
  onOpenOrder,
  onOpenCatalog,
  onBack,
}: {
  lang: StoreV2Lang;
  sessionKey: string;
  onOpenOrder: (orderId: string) => void;
  onOpenCatalog: () => void;
  onBack?: () => void;
}) {
  const [orders, setOrders] = useState<CustomerOrderSummary[] | null>(() => getCachedOrdersList(sessionKey));
  const [hardError, setHardError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const isLoading = orders == null && !hardError;
  const { showSkeleton, timedOut } = useRouteLoadingGate(isLoading);
  const groupedOrders = useMemo(
    () => (orders?.length ? groupOrdersByDate(orders, lang) : []),
    [orders, lang],
  );

  useEffect(() => {
    emitEvent("orders_list_opened", {});
  }, []);

  useEffect(() => {
    // R2-R7-P0 — restore scroll after order-detail → Back (no catalog reload).
    try {
      const y = Number(sessionStorage.getItem("sv2.orders.scroll") || "0");
      if (y > 0) {
        requestAnimationFrame(() => {
          const root =
            document.querySelector<HTMLElement>("[data-store-scroll-root='true']") ?? document.documentElement;
          root.scrollTo({ top: y, behavior: "auto" });
        });
      }
    } catch {
      /* ignore */
    }
  }, [orders]);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedOrdersList(sessionKey);
    if (cached) setOrders(cached);
    void fetchMyOrdersBounded(sessionKey).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setOrders(res.data);
        setHardError(false);
        const top = res.data[0];
        if (top) prefetchOrderDetail(sessionKey, top.order_id);
      } else if (!cached) {
        setHardError(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, attempt]);

  const retry = () => {
    setHardError(false);
    setAttempt((n) => n + 1);
  };

  const openOrder = (orderId: string) => {
    try {
      const root =
        document.querySelector<HTMLElement>("[data-store-scroll-root='true']") ?? document.documentElement;
      sessionStorage.setItem("sv2.orders.scroll", String(root.scrollTop || 0));
    } catch {
      /* ignore */
    }
    onOpenOrder(orderId);
  };

  return (
    <div className="sv2-body sv2-route-top" data-route-top="orders" data-testid="sv2-my-orders">
      <div className="sv2-section-title">{lang === "en" ? "My orders" : "Мої замовлення"}</div>
      {orders == null ? (
        timedOut || hardError ? (
          <div data-testid="sv2-orders-load-error">
            <EmptyState
              emoji="🧾"
              title={lang === "en" ? "Could not load orders." : "Не вдалося завантажити замовлення."}
              hint={lang === "en" ? "Try again or go back." : "Спробуйте ще раз або поверніться назад."}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", justifyContent: "center" }}>
              <button type="button" className="sv2-cta" data-testid="sv2-orders-retry" onClick={retry}>
                {lang === "en" ? "Try again" : "Спробувати ще раз"}
              </button>
              <button type="button" className="sv2-btn-secondary" data-testid="sv2-orders-back" onClick={onBack ?? onOpenCatalog}>
                {lang === "en" ? "Back" : "Назад"}
              </button>
            </div>
          </div>
        ) : showSkeleton ? (
          <LoadingBlock label={lang === "en" ? "Loading orders…" : "Завантажуємо замовлення…"} />
        ) : null
      ) : orders.length === 0 ? (
        <>
          <EmptyState
            emoji="🧾"
            title={lang === "en" ? "No orders yet" : "Замовлень поки немає"}
            hint={
              lang === "en"
                ? "Your orders will appear here after checkout."
                : "Ваші замовлення зʼявляться тут після оформлення."
            }
          />
          <button type="button" className="sv2-cta" style={{ marginTop: 14 }} onClick={onOpenCatalog}>
            {lang === "en" ? "Go to catalog" : "До каталогу"}
          </button>
        </>
      ) : (
        <div className="sv2-orders-list" data-testid="sv2-orders-list">
          {groupedOrders.map((group) => (
            <section key={group.label} className="sv2-orders-date-group" data-testid="sv2-orders-date-group">
              <div className="sv2-orders-date-label">{group.label}</div>
              {group.orders.map((order) => (
                <button
                  key={order.order_id}
                  type="button"
                  className="sv2-info-card sv2-order-list-item"
                  data-testid="sv2-order-list-item"
                  data-rail={order.status_rail ?? (order.order_top_status === "canceled" ? "cancelled" : order.order_top_status === "completed" ? "completed" : String(order.payment_status ?? "").startsWith("paid") ? "paid" : "awaiting")}
                  onClick={() => openOrder(order.order_id)}
                >
                  <div className="sv2-order-list-row">
                    <strong>№ {order.display_order_number}</strong>
                    <span className="sv2-muted">
                      {formatOrderReceivedCardTime(getOrderReceivedAt(order as never))}
                    </span>
                  </div>
                  <div className="sv2-status-line" data-testid="sv2-order-list-status">
                    <strong>
                      {(() => {
                        const stage = mapToCanonicalCustomerStage({
                          order_top_status: order.order_top_status,
                          status: order.status,
                          ttn_status: (order as { ttn_status?: string | null }).ttn_status ?? null,
                        });
                        const bankAt =
                          (order as { bank_confirmed_at?: string | null }).bank_confirmed_at ?? null;
                        const payPaid = Boolean(String(bankAt ?? "").trim()) ||
                          String(order.payment_status ?? "").toLowerCase().startsWith("paid") ||
                          String(order.payment_status ?? "").includes("payment_confirmed");
                        const orderLabel = canonicalOrderTitle(lang, stage);
                        if (payPaid && stage !== "CANCELLED") {
                          return orderLabel;
                        }
                        return canonicalDisplayChipUkForOrder(
                          projectCanonicalDisplayState({
                            order_top_status: order.order_top_status,
                            payment_status: order.payment_status,
                            payment_method: order.payment_method,
                            display_state: order.display_state ?? null,
                            payment_timing: (order as { payment_timing?: string | null }).payment_timing ?? null,
                            bank_confirmed_at: bankAt,
                          }),
                          (order as { payment_timing?: string | null }).payment_timing ?? null,
                        );
                      })()}
                    </strong>
                  </div>
                  {order.preview_item || (order.thumbnails?.length ?? 0) > 0 ? (
                    <div className="sv2-order-list-preview" data-testid="sv2-order-list-preview">
                      <OrderItemThumb
                        imageUrl={order.preview_item?.image_url ?? order.thumbnails?.[0] ?? null}
                        alt={order.preview_item?.name ?? order.display_order_number}
                        className="sv2-order-thumb-sm"
                      />
                      <div className="sv2-order-list-preview-text">
                        <div className="sv2-order-list-preview-title" data-testid="sv2-order-list-preview-title">
                          {order.preview_item?.name ??
                            (lang === "en" ? "Ordered items" : "Товари в замовленні")}
                        </div>
                        <div className="sv2-muted" data-testid="sv2-order-list-preview-qty">
                          × {order.preview_item?.qty ?? 1}
                          {(order.items_count ?? 0) > 1 || (order.other_items_count ?? 0) > 0 ? (
                            <span>
                              {" "}
                              ·{" "}
                              {lang === "en"
                                ? `+${Math.max(0, (order.items_count ?? 1) - 1)} more`
                                : `+ ще ${Math.max(0, (order.items_count ?? 1) - 1)} товарів`}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="sv2-order-list-row">
                    <span className="sv2-muted">
                      {(() => {
                        const timing = (order as { payment_timing?: string | null }).payment_timing ?? null;
                        const state = projectCanonicalDisplayState({
                          order_top_status: order.order_top_status,
                          payment_status: order.payment_status,
                          payment_method: order.payment_method,
                          display_state: order.display_state ?? null,
                          payment_timing: timing,
                          bank_confirmed_at: (order as { bank_confirmed_at?: string | null }).bank_confirmed_at ?? null,
                        });
                        const chip = canonicalDisplayChipUkForOrder(state, timing);
                        const sub = canonicalListSubtitleUk(state) ?? "";
                        return sub && sub !== chip ? sub : "";
                      })()}
                    </span>
                    <span>{order.total_uah != null ? formatUah(order.total_uah) : "—"}</span>
                  </div>
                  {hideCustomerReceiptLine(
                    projectCanonicalDisplayState({
                      order_top_status: order.order_top_status,
                      payment_status: order.payment_status,
                      payment_method: order.payment_method,
                      display_state: order.display_state ?? null,
                      payment_timing: (order as { payment_timing?: string | null }).payment_timing ?? null,
                      bank_confirmed_at: order.bank_confirmed_at ?? null,
                    }),
                    order.receipt.state,
                  ) ? null : order.payment_method === "bank_transfer_fop" ||
                    /iban|fop|bank_transfer/i.test(String(order.payment_method ?? "")) ? (
                  <div className="sv2-muted" style={{ fontSize: 12 }} data-testid="sv2-order-list-documents">
                    {t(lang, "documents_section")}
                  </div>
                  ) : (
                  <div className="sv2-muted" style={{ fontSize: 12 }} data-testid="sv2-order-list-receipt">
                    🧾 {receiptStateLabel(lang, order.receipt.state)}
                    {order.receipt.state === "ready" || order.receipt.state === "fiscalized" ? (
                      <span
                        className="sv2-receipt-ready-badge"
                        data-testid="sv2-order-list-receipt-ready-badge"
                        style={{ marginLeft: 6, fontWeight: 700 }}
                      >
                        {lang === "en" ? "View receipt" : "Переглянути чек"}
                      </span>
                    ) : null}
                  </div>
                  )}
                </button>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
